import React, { useCallback } from 'react';

import { useSelector } from 'react-redux';

import { Box, Grid, Stack } from '@atlaskit/primitives';
import { StatsigFeatureKeys } from '@atlassian/bitbucket-features';

import { panelStyles } from 'src/components/sidebar/src/expandable-panel';
import isMobile from 'src/components/sidebar/src/is-mobile';
import { SidebarChildContext } from 'src/components/sidebar/src/sidebar';
import { ConnectPullRequestWebCards } from 'src/connect/web-cards';
import ConnectPullRequestViewWebPanels from 'src/connect/web-panels/containers/pr-web-panels';
import {
  ConversationsContextProvider,
  useConversationsContext,
} from 'src/contexts/conversations-context';
import { useStatsigGate } from 'src/contexts/feature-provider';
import { LazyForgeRepoPullRequestCards } from 'src/forge/repo-pull-request-cards/lazy-repo-pull-request-cards';
import { LazyForgeRepoPullRequestOverviewPanel } from 'src/forge/repo-pull-request-overview-panel/lazy-repo-pull-request-overview-panel';
import { useMediaAuth } from 'src/hooks/media';
import { getCurrentPullRequest } from 'src/redux/pull-request/selectors';
import { useParams } from 'src/router/hooks';
import JiraIssuesCard from 'src/sections/repository/sections/pull-request/components/jira-issues-card';
import PullRequestOverviewActivity from 'src/sections/repository/sections/pull-request/containers/pull-request-overview-activity';
import { getIsPullRequestLabelsEnabled } from 'src/selectors/feature-selectors';
import { getCurrentRepository } from 'src/selectors/repository-selectors';
import { getCurrentUser } from 'src/selectors/user-selectors';

import CodeInsights from '../../components/code-insights';
import { PullRequestTasks } from '../../containers/tasks';
import AttachmentsPanel from '../attachments';
import { DescriptionPanel } from '../description-panel';
import { LabelsCard } from '../labels-card/labels-card';
import { MergeChecksCard } from '../merge-checks-card';
import { PullRequestReviewerStatusesCard } from '../reviewer-statuses-card/pull-request-reviewer-statuses-card';
import { PullRequestStateMessage } from '../state-message';
import { TaskMode } from '../tasks/tasks';

import { ActivityFeedPanel } from './overview/activity-panel/activity-panel';
import { PrBuildStatusCard } from './overview/pr-build-status-card';
import { SyncBranchInfo } from './sync-branch-info';

// Copied over from ../components/sidebar.tsx and the selectors in ../containers/sidebar.ts
// Redux usage should probaby be improved
function ConnectedForgePrCard() {
  const currentUser = useSelector(getCurrentUser);
  const pullRequest = useSelector(getCurrentPullRequest);
  const repository = useSelector(getCurrentRepository);

  if (repository && repository.workspace && pullRequest) {
    return (
      <LazyForgeRepoPullRequestCards
        currentUser={currentUser === null ? undefined : currentUser}
        pullRequest={pullRequest}
        repository={repository}
        workspace={repository.workspace}
      />
    );
  }
  return null;
}

type PROverviewTabProps = {
  isLoadingConflicts: boolean;
};

export function PROverviewTab({
  isLoadingConflicts,
}: PROverviewTabProps): JSX.Element {
  const { repositoryOwner, repositorySlug, pullRequestId } = useParams<{
    repositoryOwner: string;
    repositorySlug: string;
    pullRequestId: number;
  }>();

  const isPRLabelsEnabled = useSelector(getIsPullRequestLabelsEnabled);

  const repositoryFullSlug = `${repositoryOwner}/${repositorySlug}`;

  const isMobileView = isMobile();

  const mediaAuth = useMediaAuth({
    repositoryFullSlug,
  });

  const isForgePrOverviewPanelEnabled = useStatsigGate(
    StatsigFeatureKeys.useForgePrOverviewPanel
  );

  const assignCardIndices = useCallback(
    (card: React.ReactNode, index: number) => {
      return (
        <SidebarChildContext.Provider value={index} key={index}>
          {card}
        </SidebarChildContext.Provider>
      );
    },
    []
  );

  const sidebarCards = [];

  sidebarCards.push(
    <PrBuildStatusCard
      repositoryOwner={repositoryOwner}
      repositorySlug={repositorySlug}
      pullRequestId={pullRequestId}
    />
  );

  sidebarCards.push(<MergeChecksCard />);

  sidebarCards.push(<PullRequestReviewerStatusesCard />);

  sidebarCards.push(
    <PullRequestTasks initialMode={TaskMode.Read} isSidebarCollapsed={false} />
  );

  if (isPRLabelsEnabled) {
    sidebarCards.push(<LabelsCard />);
  }

  sidebarCards.push(<JiraIssuesCard />);

  sidebarCards.push(<CodeInsights />);

  sidebarCards.push(<ConnectPullRequestWebCards />);

  sidebarCards.push(<ConnectedForgePrCard />);

  const conversationContext = useConversationsContext();

  const MainPanelContent = (
    <Stack grow="fill">
      <DescriptionPanel />
      <AttachmentsPanel
        repositoryFullSlug={repositoryFullSlug}
        pullRequestId={pullRequestId}
        mediaAuth={mediaAuth}
      />
      <SyncBranchInfo isLoadingConflicts={isLoadingConflicts} />
      {isForgePrOverviewPanelEnabled && (
        <LazyForgeRepoPullRequestOverviewPanel />
      )}
      <Box xcss={panelStyles}>
        <ConnectPullRequestViewWebPanels />
      </Box>
      <ConversationsContextProvider
        value={{
          ...conversationContext,
          activityFeedVersion: 'new',
        }}
      >
        {isMobileView ? (
          <ActivityFeedPanel mediaAuth={mediaAuth} />
        ) : (
          <Box xcss={panelStyles}>
            <PullRequestOverviewActivity mediaAuth={mediaAuth} />
          </Box>
        )}
      </ConversationsContextProvider>
    </Stack>
  );

  const SidebarPanelContent = (
    <aside>
      <Stack grow="fill" testId="pr-cards-list">
        {sidebarCards.map(assignCardIndices)}
      </Stack>
    </aside>
  );

  return (
    <Stack grow="fill">
      {isMobileView ? (
        <>
          {!isLoadingConflicts && <PullRequestStateMessage />}
          <Box role="region">
            {MainPanelContent}
            {SidebarPanelContent}
          </Box>
        </>
      ) : (
        /*
        minmax() is needed to actually constrain the column dimensions. If we
        just use the fractional values, the columns will do whatever they want to
        do when child elements try to expand. We really want this top-level grid
        to have a fixed size so that children have a clear sizing to adhere to.
        */
        <Grid gap="space.400" templateColumns="minmax(0, 3fr) minmax(0, 1fr)">
          <Stack>
            {/* Left side w/ primary content */}
            {!isLoadingConflicts && <PullRequestStateMessage />}
            <Box role="region">{MainPanelContent}</Box>
          </Stack>
          {/* Right side w/ additional PR detail cards */}
          <Box role="region">{SidebarPanelContent}</Box>
        </Grid>
      )}
    </Stack>
  );
}
